import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface HospitalComparisonChartProps {
  code: string;
  codeType: string;
  setting?: string;
  unit?: string;
  limit?: number;
}

/**
 * Live hospital comparison chart widget that shows top hospitals
 * ranked by price for a specific procedure/code.
 *
 * This component is embedded in educational HTML content.
 */
const HospitalComparisonChart: React.FC<HospitalComparisonChartProps> = ({
  code,
  codeType,
  setting = 'all',
  unit = 'all',
  limit = 20
}) => {
  const { data: hospitalData, isLoading } = useQuery({
    queryKey: ['hospital-comparison', code, codeType, setting, unit],
    queryFn: async () => {
      const codeTypeLower = codeType.toLowerCase();

      // Query hospital_procedure_analytics - single table for all code types
      let query = supabase
        .from('hospital_procedure_analytics')
        .select('hospital_id, hospital_gross_charge, normalized_unit, inpatient_outpatient')
        .eq('procedure_code', code)
        .eq('procedure_type', codeTypeLower);

      // Filter by setting if specified (normalize to lowercase for comparison)
      if (setting && setting !== 'all') {
        // Match both 'outpatient' and 'Outpatient', 'OUTPATIENT', 'both', 'Both', etc.
        const settingFilter = setting.toLowerCase();
        if (settingFilter === 'inpatient') {
          query = query.or('inpatient_outpatient.ilike.inpatient,inpatient_outpatient.ilike.both');
        } else if (settingFilter === 'outpatient') {
          query = query.or('inpatient_outpatient.ilike.outpatient,inpatient_outpatient.ilike.both');
        }
      }

      // Filter by unit if specified
      if (unit && unit !== 'all') {
        query = query.eq('normalized_unit', unit);
      }

      const { data: pricingData, error } = await query
        .not('hospital_gross_charge', 'is', null)
        .order('hospital_gross_charge', { ascending: false })
        .limit(limit);

      if (error) throw error;
      if (!pricingData || pricingData.length === 0) return [];

      // Fetch hospital names
      const hospitalIds = pricingData.map(row => row.hospital_id);
      const { data: hospitals } = await supabase
        .from('hospitals')
        .select('id, name')
        .in('id', hospitalIds);

      if (!hospitals) return [];

      const hospitalMap = new Map(hospitals.map(h => [h.id, h.name]));

      return pricingData
        .map(row => ({
          hospitalName: hospitalMap.get(row.hospital_id) || 'Unknown Hospital',
          price: Number(row.hospital_gross_charge || 0)
        }))
        .filter(row => row.price > 0)
        .sort((a, b) => b.price - a.price);
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  if (isLoading) {
    return (
      <div className="space-y-2 my-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!hospitalData || hospitalData.length === 0) {
    return (
      <div className="border border-matrix-green/20 rounded-lg p-4 my-4 text-center bg-black/20">
        <p className="text-sm text-muted-foreground">
          Hospital comparison data is being calculated. Check back soon!
        </p>
      </div>
    );
  }

  const maxPrice = Math.max(...hospitalData.map(h => h.price));
  const minPrice = Math.min(...hospitalData.map(h => h.price));
  const avgPrice = hospitalData.reduce((sum, h) => sum + h.price, 0) / hospitalData.length;

  return (
    <div className="my-6 border border-matrix-green/20 rounded-lg overflow-hidden bg-black/20">
      <div className="bg-black/60 px-4 py-3 border-b border-matrix-green/20">
        <h4 className="font-mono font-bold text-matrix-green text-sm mb-2">
          🏥 Hospital Price Comparison (Live Data)
        </h4>
        <div className="flex gap-4 text-xs">
          <div>
            <span className="text-muted-foreground">Hospitals: </span>
            <span className="font-mono font-bold">{hospitalData.length}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Avg: </span>
            <span className="font-mono font-bold text-matrix-green">
              ${avgPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Range: </span>
            <span className="font-mono font-bold">
              ${minPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })} - ${maxPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
        {hospitalData.map((hospital, idx) => {
          const barWidth = (hospital.price / maxPrice) * 100;
          const isExpensive = hospital.price > avgPrice * 1.5;
          const isCheap = hospital.price < avgPrice * 0.7;

          return (
            <div key={idx} className="flex items-center gap-2">
              <div className="w-8 text-right">
                <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium truncate" title={hospital.hospitalName}>
                    {hospital.hospitalName}
                  </span>
                  {isExpensive && (
                    <Badge variant="destructive" className="text-xs ml-2">
                      High Cost
                    </Badge>
                  )}
                  {isCheap && (
                    <Badge variant="secondary" className="text-xs ml-2 bg-green-900/30 text-green-400 border-green-500/50">
                      Best Value
                    </Badge>
                  )}
                </div>
                <div className="relative h-8 bg-black/60 rounded overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 h-full transition-all ${
                      isExpensive ? 'bg-gradient-to-r from-red-500/80 to-red-500/60' :
                      isCheap ? 'bg-gradient-to-r from-green-500/80 to-green-500/60' :
                      'bg-gradient-to-r from-matrix-green/80 to-matrix-green/60'
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-end px-3">
                    <span className="font-mono font-bold text-white text-xs drop-shadow-lg">
                      ${hospital.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-black/40 px-4 py-2 text-xs text-muted-foreground border-t border-matrix-green/20">
        💡 Showing top {limit} hospitals by price. Data updated daily from hospital transparency reports.
      </div>
    </div>
  );
};

export default HospitalComparisonChart;
